import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileUp, CheckCircle2, AlertCircle, Loader2, Download, Table } from "lucide-react";
import { toast } from "sonner";
import { attendanceService } from "@/services/attendanceService";
import { useAuth } from "@/hooks/useAuth";
import * as XLSX from "xlsx";

const AttendanceUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<any[] | null>(null);
  const { company } = useAuth();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        identificación: "12345678",
        fecha: "2026-03-20",
        entrada: "08:00",
        salida: "17:30"
      },
      {
        identificación: "87654321",
        fecha: "2026-03-20",
        entrada: "08:15",
        salida: "18:00"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Asistencia");
    XLSX.writeFile(wb, "Plantilla_Asistencia.xlsx");
    toast.info("Plantilla descargada. Recuerda usar el formato YYYY-MM-DD para fechas.");
  };

  const uploadFile = async () => {
    if (!file) {
      toast.error("Por favor selecciona un archivo");
      return;
    }
    if (!company?.slug) {
      toast.error("No se encontró la información de la empresa.");
      return;
    }

    setIsUploading(true);
    setUploadResults(null);

    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = async () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1];
        
        try {
          const response = await attendanceService.uploadAttendanceExcel({
            slug: company.slug,
            file_base64: base64
          });
          
          setUploadResults(response.data.data);
          toast.success("Archivo procesado correctamente");
        } catch (error: any) {
          console.error("Error uploading file:", error);
          toast.error(error.response?.data?.message || "Error al subir el archivo");
        } finally {
          setIsUploading(false);
        }
      };
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Error al leer el archivo");
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex justify-end gap-3">
        <Button 
          variant="outline" 
          onClick={downloadTemplate}
          className="h-10 border-slate-200 text-slate-600 hover:bg-slate-50 gap-2"
        >
          <Download className="h-4 w-4" />
          Descargar Plantilla (.xlsx)
        </Button>
      </div>

      <Card className="border-slate-200/60 shadow-sm overflow-hidden bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <FileUp className="h-5 w-5 text-blue-600" />
            Carga Masiva de Asistencia
          </CardTitle>
          <CardDescription className="text-slate-600">
            Sube un archivo Excel con las columnas: **identificación**, **fecha**, **entrada** y **salida**.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl p-12 hover:border-blue-500/50 hover:bg-blue-50/50 transition-all group relative">
            <Input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              id="attendance-file"
              onChange={handleFileChange}
            />
            <label
              htmlFor="attendance-file"
              className="flex flex-col items-center gap-4 cursor-pointer"
            >
              <div className="p-4 bg-slate-50 rounded-full group-hover:bg-blue-600 transition-colors shadow-inner">
                <Upload className="h-8 w-8 text-slate-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-slate-900">
                  {file ? file.name : "Haz clic para seleccionar o arrastra un archivo"}
                </p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Table className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Formato XLSX Soportado</span>
                </div>
              </div>
            </label>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={uploadFile}
              disabled={!file || isUploading}
              className="bg-blue-600 hover:bg-blue-700 text-white min-w-[240px] h-12 rounded-xl shadow-lg shadow-blue-500/20 border-0 text-md font-bold transition-all hover:scale-[1.02] active:scale-95"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                  Procesando Registros...
                </>
              ) : (
                "Subir y Procesar Asistencias"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {uploadResults && (
        <Card className="border-slate-200/60 shadow-md bg-white animate-in fade-in slide-in-from-top-4 duration-500 overflow-hidden">
          <CardHeader className="bg-slate-50/50 border-b border-slate-100">
            <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Resultados de la Carga
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
              {uploadResults.map((result: any, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {result.status === "success" ? (
                      <div className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-rose-500" />
                    )}
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        ID: {result.identification} | <span className="text-slate-500 font-normal">{result.date}</span>
                      </p>
                      <p className={`text-xs ${result.status === "success" ? "text-emerald-600" : "text-rose-600"} font-medium`}>
                        {result.status === "success" ? "✓ Registro cargado exitosamente" : `✗ ${result.message}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AttendanceUpload;
